import { ApolloProvider } from '@apollo/client';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useEffect } from 'react';
import { Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom';

import { Layout } from './components/layout';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { ConfirmationProvider } from './components/ui/confirmation-dialog';
import './i18n'; // Initialize i18n

import { apolloClient } from '@/lib/apolloClient';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AlertSubscriptionProvider } from '@/contexts/AlertSubscriptionContext';
import { EntityModalProvider } from '@/contexts/EntityModalContext';
import { Login } from '@/pages/auth/login';
import { DetachedEntityView } from '@/pages/detached/detached-entity-view';
import { sentryCreateBrowserRouter } from './sentry';

ModuleRegistry.registerModules([AllCommunityModule]);

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { isAuthenticated, isLoading } = useAuth();
	const location = useLocation();

	// Only redirect after auth has resolved
	if (!isAuthenticated && !isLoading) {
		return <Navigate to="/login" replace state={{ from: location }} />;
	}

	if (!isAuthenticated) return null;

	return <>{children}</>;
};

const ProtectedRoutes = () => (
	<RequireAuth>
		<Layout />
	</RequireAuth>
);

// Dismiss inline HTML splash once auth has resolved
const SplashDismisser = () => {
	const { isLoading } = useAuth();
	useEffect(() => {
		if (!isLoading) {
			(window as unknown as Record<string, (() => void) | undefined>).__vectroSplashReady?.();
		}
	}, [isLoading]);
	return null;
};

// Root layout that wraps all routes with EntityModalProvider (inside Router context)
const RootLayout = () => (
	<EntityModalProvider>
		<SplashDismisser />
		<Outlet />
		<Toaster />
	</EntityModalProvider>
);

const router = sentryCreateBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			{
				path: '/login',
				element: <Login />
			},
			{
				path: '/view/:entityType/:entityId',
				element: (
					<RequireAuth>
						<DetachedEntityView />
					</RequireAuth>
				)
			},
			{
				path: '/*',
				element: <ProtectedRoutes />
			}
		]
	}
], {
	future: {
		v7_relativeSplatPath: true,
		v7_fetcherPersist: true,
		v7_normalizeFormMethod: true,
		v7_partialHydration: true,
		v7_skipActionErrorRevalidation: true
	}
});

function App() {
	return (
		<ApolloProvider client={apolloClient}>
			<AuthProvider>
				<AlertSubscriptionProvider>
					<ThemeProvider>
						<ConfirmationProvider>
							<RouterProvider router={router} future={{ v7_startTransition: true }} />
						</ConfirmationProvider>
					</ThemeProvider>
				</AlertSubscriptionProvider>
			</AuthProvider>
		</ApolloProvider>
	);
}

export default App;
